import { map, flatMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  DataService,
  ApiService,
  InspectionPluginService,
  MachineFamillyService,
  ActionPlanDataService,
  LanguageService
} from '../../shared/services/index';
import { Icons } from '../../shared/models';
import * as _ from 'lodash';
import { ObservationsAndRecommandationsFactoryService } from './observationandrecommandations-plugin-factory.service';
import { PartsAddPageComponent } from '../parts/parts-add.component';

@Injectable()
export class ObservationsAndRecommandationsPluginService extends InspectionPluginService {
  public responsiblesKeys: any = [
    { value: -1, label: 'plugins.observation.Aucun' },
    { value: 0, label: 'plugins.observation.Bobst' },
    { value: 1, label: 'plugins.observation.Customer' },
    { value: 2, label: 'plugins.observation.Shared' }
  ];

  public responsibles: any = [];

  public prioritiesKeys: any = [
    { value: 0, label: 'plugins.observation.High' },
    { value: 1, label: 'plugins.observation.Medium' },
    { value: 2, label: 'plugins.observation.Low' },
    { value: -1, label: 'plugins.observation.Aucun' }
  ];

  public priorities: any = [];

  public machines: any[];

  constructor(
    protected apiService: ApiService,
    protected dataService: DataService,
    private machineService: MachineFamillyService,
    private actionPlanData: ActionPlanDataService,
    private language: LanguageService,
    private observationFacotry: ObservationsAndRecommandationsFactoryService
  ) {
    super(apiService, dataService, null, observationFacotry);
    this.machineService.getModels().subscribe((data) => (this.machines = data));

    this.applyTranslation();
    this.language.onLanguageChange().subscribe((value) => {
      this.applyTranslation();
    });
  }

  public updateObservationsAndRecommandations(
    inspectionId: string,
    pointId: string,
    pluginId: string,
    data: any,
    messageInfo: any
  ): Observable<any> {
    return this._updateObservationsAndRecommandations(inspectionId, pointId, pluginId, data, messageInfo).pipe(
      map((res: any) => {
        if (res.plugin.config && res.plugin.config.ObservationsAndRecommandations) {
          this.dataService.processIcons(
            res.plugin.config.ObservationsAndRecommandations.some((o) => o.IsInActionPlan && o.Selected),
            Icons.getIconByName(Icons.observationIconName),
            inspectionId,
            pointId
          );
          this.dataService.processIcons(
            res.plugin.config.ObservationsAndRecommandations.some((o) => o.Parts && o.Parts.length),
            Icons.getIconByName(Icons.hasPartsIconName),
            inspectionId,
            pointId
          );
        }
        this.updateActionPlan(res, inspectionId);
        this.dataService.setChangedPoint(pointId);
        return this.dataService.updatePlugin(inspectionId, res.plugin);
      })
    );
  }

  private updateActionPlan(res: any, inspectionId: string) {
    const inspection = this.dataService.getInspection(inspectionId);
    if (inspection && inspection.actionPlanId) {
      if (res.items) {
        res.items = this.actionPlanData.updateActionPlanItems(inspection.actionPlanId, res.items);
      }
      if (res.plugins) {
        this.actionPlanData.addPlugin(inspection.actionPlanId, res.plugins);
      }
      if (res.plugin && res.plugin.config && res.plugin.config.ObservationsAndRecommandations) {
        const observationIdsToRemove = res.plugin.config.ObservationsAndRecommandations.filter(
          (o) => !o.IsInActionPlan || !o.Selected
        ).map((o) => o.Id);
        this.actionPlanData.removeActionPlanItemsByObservationIds(inspection.actionPlanId, observationIdsToRemove);
      }
    }
  }

  public getResponsibles() {
    return this.responsibles;
  }

  public getPriorities() {
    return this.priorities;
  }

  public getMachines() {
    return this.machines;
  }

  getMachineName(id: string): string {
    const obj = _.find(this.getMachines(), ['id', id]);
    return obj ? obj.name : '';
  }

  getMachineId(name: string): string {
    const obj = _.find(this.getMachines(), ['name', name]);
    return obj ? obj.id : '';
  }

  public deletePlugin(inspectionId: string, pointId: string, pluginId: string): any {
    return super.deletePlugin(inspectionId, pointId, pluginId).pipe(
      map((success) => {
        this.dataService.processIcons(false, Icons.getIconByName(Icons.observationIconName), inspectionId, pointId);
        this.dataService.processIcons(false, Icons.getIconByName(Icons.hasPartsIconName), inspectionId, pointId);
        return success;
      })
    );
  }

  private _updateObservationsAndRecommandations(
    inspectionId: string,
    pointId: string,
    pluginId: string,
    observations: any,
    messageInfo: any
  ): Observable<any> {
    const data = { ObservationsAndRecommandations: observations, MessageInfo: messageInfo };
    return this.observationFacotry.updateObservationsAndRecommandations(inspectionId, pointId, pluginId, data).pipe(
      flatMap((plugin) => {
        if (this.isInspectionInOffline(inspectionId)) {
          return of(plugin);
        }
        return this.apiService.post(plugin.plugin.postServerUrl, plugin.plugin.config);
      })
    );
  }

  private applyTranslation() {
    this.responsibles = [];
    this.priorities = [];
    this.copyTranslationValues(this.responsiblesKeys, this.responsibles, this.language);
    this.copyTranslationValues(this.prioritiesKeys, this.priorities, this.language);
  }
}
